<!--
  AboutProjectModal.vue

  Purpose: give colorize.design real, crawlable text content for SEO and LLMs.
  The tool UI itself is almost text-free, so search engines and AI crawlers had
  little to understand or cite. This modal holds a keyword-targeted description
  of the tool, a how-to, feature list, export formats and an FAQ.

  IMPORTANT (why v-show, not v-if): the content is toggled with `v-show`, so it
  is server-rendered into the page HTML and stays in the DOM even while hidden.
  That is what makes it readable by Googlebot and AI crawlers. Switching to
  `v-if` (or the HeadlessUI Dialog show/hide pattern used elsewhere) would unmount
  the content and remove the SEO/LLM benefit — don't do that here.

  The FAQ array is the single source of truth for both the visible list and the
  FAQPage JSON-LD, so they can never drift apart.
-->
<template>
  <Transition name="about">
    <div
      v-show="isOpen"
      class="fixed inset-0 z-[70]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="about-modal-title"
    >
      <!-- Dimmed backdrop (visual only; the wrapper below handles click-to-close) -->
      <div class="absolute inset-0 bg-gray-950/80 backdrop-blur-sm"></div>

      <!-- Scroll + centering layer. @click.self closes on clicks outside the panel. -->
      <div class="absolute inset-0 overflow-y-auto">
        <div
          class="flex min-h-full items-start justify-center p-4 sm:items-center"
          @click.self="close"
        >
          <div
            ref="panelRef"
            tabindex="-1"
            class="about-panel relative w-full max-w-3xl rounded-xl bg-gray-950 text-left shadow-2xl ring-1 ring-white/10 focus:outline-none"
          >
            <!-- Sticky header with title + close — the same header BaseModal
                 draws, so the two dialogs match. -->
            <div class="sticky top-0 z-10 flex items-center justify-between gap-4 rounded-t-xl border-b border-gray-900 bg-gray-950 px-5 py-4">
              <h2 id="about-modal-title" class="text-lg font-semibold text-white">
                About colorize.design
              </h2>
              <CloseButton class="-mr-1.5" @click="close" />
            </div>

            <!-- Body: the SEO/LLM content -->
            <div class="max-h-[75vh] overflow-y-auto px-5 py-5 text-base leading-7 text-gray-300 sm:px-6">
              <img
                :src="imgOverview"
                alt="Enter a website URL into colorize.design and generate a color palette of the site's essential colors"
                loading="lazy"
                decoding="async"
                class="block aspect-[1270/650] w-full rounded-lg object-cover object-top"
              />

              <p class="mt-5">
                <strong class="text-white">colorize.design</strong> is a free online
                color palette extractor that pulls the real colors out of any website.
                Paste a URL and the tool instantly generates a color palette from the
                site's brand colors, logo, homepage screenshot, and its underlying HTML,
                CSS, and JavaScript &mdash; no sign-up required. It's built for designers,
                developers, and brand teams who need to extract a color scheme from a
                website in seconds and export it in the format their tools expect.
              </p>


              <h3 class="mt-7 text-base font-semibold text-white">
                How to extract a color palette from a website
              </h3>
              <ol class="mt-3 list-decimal space-y-2 pl-5 marker:text-gray-500">
                <li>Copy the URL of the website you want colors from.</li>
                <li>Paste it into the box at the top of colorize.design and press <em>Generate</em>.</li>
                <li>Review the extracted palette &mdash; grouped into brand colors, logo colors, homepage colors, and the nearest named colors.</li>
                <li>Select the swatches you want and copy any hex code with one click, or download the whole palette.</li>
              </ol>
              <p class="mt-3 text-gray-400">
                That's it &mdash; you can pull colors from a website and build a reusable
                palette in under ten seconds.
              </p>

              <img
                :src="imgCustomize"
                alt="Select, copy, save or delete swatches to customize your extracted color palette"
                loading="lazy"
                decoding="async"
                class="mt-5 block aspect-[1270/650] w-full rounded-lg object-cover object-top"
              />

              <h3 class="mt-7 text-base font-semibold text-white">
                What colorize.design extracts
              </h3>
              <ul class="mt-3 space-y-2">
                <li class="flex gap-2">
                  <span class="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-sky-400"></span>
                  <span><strong class="font-semibold text-white">Brand colors</strong> &mdash; the primary and secondary colors a site uses consistently, detected automatically.</span>
                </li>
                <li class="flex gap-2">
                  <span class="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-sky-400"></span>
                  <span><strong class="font-semibold text-white">Logo colors</strong> &mdash; the dominant colors pulled from the brand's logo.</span>
                </li>
                <li class="flex gap-2">
                  <span class="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-sky-400"></span>
                  <span><strong class="font-semibold text-white">Homepage colors</strong> &mdash; colors sampled from a live screenshot of the site's homepage.</span>
                </li>
                <li class="flex gap-2">
                  <span class="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-sky-400"></span>
                  <span><strong class="font-semibold text-white">HTML, CSS &amp; JavaScript colors</strong> &mdash; every color declared across the site's code.</span>
                </li>
                <li class="flex gap-2">
                  <span class="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-sky-400"></span>
                  <span><strong class="font-semibold text-white">Nearest named colors</strong> &mdash; each extracted color matched to its closest named color for easy reference.</span>
                </li>
              </ul>

              <img
                :src="imgSources"
                alt="colorize.design extracts colors from a website's logo, homepage screenshot, HTML and CSS"
                loading="lazy"
                decoding="async"
                class="mt-5 block aspect-[1270/650] w-full rounded-lg object-cover object-top"
              />

              <h3 class="mt-7 text-base font-semibold text-white">Add your own colors</h3>
              <p class="mt-3">
                Missed a color? Sample any pixel straight from the site's homepage
                screenshot and add it to your palette, or drop in a custom hex code &mdash;
                the easiest way to pick an exact color from a website and keep everything
                in one place.
              </p>

              <img
                :src="imgCustom"
                alt="Add custom colors by sampling any pixel from the website's homepage screenshot"
                loading="lazy"
                decoding="async"
                class="mt-5 block aspect-[1270/650] w-full rounded-lg object-cover object-top"
              />

              <h3 class="mt-7 text-base font-semibold text-white">Who uses it</h3>
              <p class="mt-3">
                Web designers use it to match a client's existing color scheme,
                developers grab exact hex and brand colors from a website to reproduce a
                design system, and brand strategists audit how a company uses color
                across its site. Anyone who needs to find the color palette of a website
                &mdash; a competitor's site, a source of inspiration, or their own &mdash;
                can do it here for free.
              </p>

              <h3 class="mt-7 text-base font-semibold text-white">Export and download formats</h3>
              <p class="mt-3">
                Download your palette in the format your workflow needs: CSS variables,
                SVG, SASS and SCSS, Adobe Swatch Exchange (ASE), Adobe Color Table (ACT),
                Adobe Color Swatch (ACO), Sketch Palette, GIMP (GPL), and more. Copy
                individual hex codes, or export the full color scheme to drop straight
                into Figma, Adobe, Tailwind, or your codebase.
              </p>

              <img
                :src="imgFormats"
                alt="Download the website color palette as CSS, SVG, SCSS, SASS, Adobe, Sketch, GIMP and more"
                loading="lazy"
                decoding="async"
                class="mt-5 block aspect-[1270/650] w-full rounded-lg object-cover object-top"
              />

              <h3 class="mt-7 text-base font-semibold text-white">colorize.design API</h3>
              <p class="mt-3">
                Need color extraction inside your own app? The colorize.design API lets
                you extract brand colors, palettes, and homepage screenshots from any URL
                programmatically. Get a free API key to integrate website color, brand,
                and screenshot extraction into your product.
                <TextLink :href="apiUrl('about_modal', route.path)" tone="accent" @click="apiClick">Get an API key &rarr;</TextLink>
              </p>

              <img
                :src="imgApi"
                alt="colorize.design API JSON response with a website's code palette and visual brand palette"
                loading="lazy"
                decoding="async"
                class="mt-5 block aspect-[1270/650] w-full rounded-lg object-cover object-top"
              />

              <h3 class="mt-7 text-base font-semibold text-white">Frequently asked questions</h3>
              <dl class="mt-3 space-y-4">
                <div v-for="faq in faqs" :key="faq.question">
                  <dt class="font-semibold text-white">{{ faq.question }}</dt>
                  <dd class="mt-1 text-gray-400">{{ faq.answer }}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount, nextTick } from 'vue'
import { trackEvent } from '~/composables/analytics'
import CloseButton from '~/components/atoms/CloseButton.vue'
import TextLink from '~/components/atoms/TextLink.vue'
import { apiUrl } from '~/utils/site'

// Feature illustrations. Imported (not string paths) so Vite bundles + hashes
// them. They sit inside the v-show'd modal and are lazy-loaded, so they only
// fetch when the modal is opened.
import imgFormats from '~/assets/img/1445.jpg'
import imgSources from '~/assets/img/1446.jpg'
import imgCustomize from '~/assets/img/1447.jpg'
import imgCustom from '~/assets/img/1448.jpg'
import imgApi from '~/assets/img/1449.jpg'
import imgOverview from '~/assets/img/1450.jpg'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue'])

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// Only to stamp the outbound Prefetch link with the page the modal was opened
// from — today that's always the generator, but the modal doesn't assume it.
const route = useRoute()

const panelRef = ref(null)
const close = () => { isOpen.value = false }
const onKeydown = (event) => { if (event.key === 'Escape') close() }

const apiClick = () => {
  try { trackEvent('About Project API Clicked') } catch { /* analytics is best-effort */ }
}

// Lock body scroll, wire up Escape, and move focus into the panel while open.
// Guarded to the client so SSR never touches `document`.
watch(isOpen, (open) => {
  if (!import.meta.client) return
  if (open) {
    document.addEventListener('keydown', onKeydown)
    document.body.style.overflow = 'hidden'
    nextTick(() => panelRef.value?.focus())
  } else {
    document.removeEventListener('keydown', onKeydown)
    document.body.style.overflow = ''
  }
})

onBeforeUnmount(() => {
  if (!import.meta.client) return
  document.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})

// Single source of truth for the visible FAQ and the FAQPage structured data below.
const faqs = [
  {
    question: 'How do I extract a color palette from a website?',
    answer:
      "Paste the website's URL into colorize.design and press Generate. The tool extracts the site's brand, logo, homepage, and code colors and builds a palette you can copy or download — free and without an account.",
  },
  {
    question: 'Is colorize.design free?',
    answer:
      'Yes. Extracting and downloading color palettes from any website is completely free and requires no sign-up.',
  },
  {
    question: 'Can I get the brand colors of a website?',
    answer:
      "Yes. colorize.design automatically detects a site's primary and secondary brand colors, plus the dominant colors in its logo.",
  },
  {
    question: 'How do I find the color palette of a website I like?',
    answer:
      "Copy its URL, paste it into colorize.design, and you'll instantly see every color the site uses — grouped and ready to export.",
  },
  {
    question: 'What formats can I export the palette in?',
    answer:
      'CSS, SVG, SASS, SCSS, Adobe (ASE, ACT, ACO), Sketch, GIMP, and more — plus one-click hex copying.',
  },
  {
    question: 'Is this a photo colorizer?',
    answer:
      'No. colorize.design extracts the real color scheme from a website URL — it does not colorize black-and-white photos. Enter any site address to pull its colors.',
  },
]

// Structured data for search engines and LLMs. Because the content above is in
// the DOM (v-show, not v-if), this JSON-LD matches on-page content as Google
// requires. WebApplication describes the tool; FAQPage mirrors `faqs`.
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebApplication',
            name: 'colorize.design',
            url: 'https://colorize.design',
            applicationCategory: 'DesignApplication',
            operatingSystem: 'Web',
            description:
              'Free online color palette extractor that generates color palettes from any website URL — brand colors, logo colors, homepage and CSS colors — with instant export to CSS, SVG, Adobe and more.',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            featureList: [
              'Extract a color palette from any website URL',
              'Detect brand colors',
              'Extract logo colors',
              'Sample homepage screenshot colors',
              'Read HTML, CSS and JavaScript colors',
              'Export to CSS, SVG, SASS, SCSS, Adobe ASE/ACT/ACO, Sketch and GIMP',
            ],
          },
          {
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
          },
        ],
      }),
    },
  ],
})
</script>

<style scoped>
/* Fade the overlay and lift the panel. Uses <Transition> over a v-show element,
   so the content stays mounted (and crawlable) while hidden. */
.about-enter-active,
.about-leave-active {
  transition: opacity 0.2s ease;
}
.about-enter-from,
.about-leave-to {
  opacity: 0;
}
.about-enter-active .about-panel,
.about-leave-active .about-panel {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.about-enter-from .about-panel,
.about-leave-to .about-panel {
  transform: translateY(0.5rem) scale(0.98);
  opacity: 0;
}
</style>
